import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, MapPin, Phone, Mail, Globe, User, Star, Award, Users, Store, Search, Lock, ChevronUp, ChevronDown, X, Check, MoreVertical, Settings, Key, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import TableSkeleton from '../components/TableSkeleton';
import HorairesManager from '../components/HorairesManager';
import CategoryPicker from '../components/CategoryPicker';
import CommerceMetierBadge from '../components/CommerceMetierBadge';
import CommercantLogoField from '../components/CommercantLogoField';
import CommercantPrinterCockpit from '../components/CommercantPrinterCockpit';
import {
  getDefaultsForCategories,
  getFilterOptionsByFamily,
  commercantMatchesFamilyFilter,
  getCommercantCategoryIds,
} from '../config/commerce-categories';
import {
  EMPTY_CATEGORY_SELECTION,
  buildEditCategoriesPayload,
  categorySelectionsEqual,
  normalizeCategorySelection,
  validateCategorySelection,
} from '../utils/categorySelection';
import { frontendToBackend, hasItinerantRetraitAddress } from '../utils/transformHoraires';
import PermissionModal from '../components/permissions/PermissionModal';
import { hasAnyPosPermission, canManagePlatformModules } from '../config/permissionDefinitions';
import useCurrentUser from '../hooks/useCurrentUser';
import useCommerceTaxonomy from '../hooks/useCommerceTaxonomy';
import { normalizeCommercantsResponse } from '../utils/commercantsList';
import {
  buildAccountsByCommercantId,
  getAccountStatusForCommercant,
  serviceBadges,
} from '../utils/commercantListUtils';
import CommercantListAvatar from '../components/CommercantListAvatar';
import { clearAssistanceSession } from '../utils/assistanceSession';
import { validateFrenchPostalCode } from '../utils/frenchPostalCode';

/** État initial création : aucun métier présélectionné (pas de RESTAURANT implicite). */
const buildEmptyNewCommercantState = () => ({
  nomBoutique: '',
  description: '',
  categorie: EMPTY_CATEGORY_SELECTION.categorie,
  categories: [...EMPTY_CATEGORY_SELECTION.categories],
  logo: null,
  logoPreview: null,
  rue: '',
  adresse2: '',
  ville: '',
  codePostal: '',
  telephoneBoutique: '',
  emailBoutique: '',
  emailNotificationCommandes: '',
  siteWeb: '',
  tempsPreparation: 15,
  tempsLivraisonMin: 30,
  tempsLivraisonMax: 45,
  fraisLivraison: 0,
  minimumCommandeLivraison: 20,
  seuilLivraisonGratuite: 0,
  dureePlageHoraireMinutes: 30,
  maxCommandesParPlage: 2,
  livraison: false,
  clickAndCollect: false,
  paiementEnLigne: true,
  paiementSurPlace: true,
  magasinItinerant: false,
  lieuPrincipal: '',
  ...getDefaultsForCategories([]),
  fermetureTemporaireDebut: '',
  fermetureTemporaireFin: '',
  raisonFermeture: '',
  imprimanteActive: false,
  imprimanteNumeroSerie: '',
  imprimanteAppId: '',
  imprimanteAppKey: '',
  imprimerCommandeValidee: true,
  imprimerCommandeAcceptee: false,
  siret: '',
  prenomGerant: '',
  nomGerant: '',
  emailGerant: '',
  telephoneGerant: '',
  password: '',
});

const mergeCategoryDefaultsIntoState = (prev, { categorie, categories, defaults }) => {
  const normalized = normalizeCategorySelection({ categorie, categories });
  return {
    ...prev,
    categorie: normalized.categorie,
    categories: normalized.categories,
    livraison: defaults.livraison ?? prev.livraison,
    clickAndCollect: defaults.clickAndCollect ?? prev.clickAndCollect,
    paiementEnLigne: defaults.paiementEnLigne ?? prev.paiementEnLigne,
    paiementSurPlace: defaults.paiementSurPlace ?? prev.paiementSurPlace,
    moduleProduits: defaults.moduleProduits,
    modulePrestations: defaults.modulePrestations,
    priseRDV: defaults.priseRDV,
    priseCreneaux: defaults.priseCreneaux,
    moduleCreneaux: defaults.moduleCreneaux,
    reservationTable: defaults.reservationTable,
    pasGrilleMinutes: defaults.pasGrilleMinutes,
    dureesAutoriseesMinutes: defaults.dureesAutoriseesMinutes,
  };
};

// Détection de l'environnement Vite
const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

const Commercants = ({ setImpersonatedUser }) => {
  const navigate = useNavigate();
  const { role: currentUserRole } = useCurrentUser();
  const { getMetier: getTaxonomyMetier } = useCommerceTaxonomy();
  const canEditPermissions = canManagePlatformModules(currentUserRole);
  const { toasts, toast, removeToast } = useToast();
  const [commercants, setCommercants] = useState([]);
  const [users, setUsers] = useState([]);
  const [accountsSummary, setAccountsSummary] = useState([]);
  const [accountsStats, setAccountsStats] = useState({ total: 0, linked: 0, missing: 0, noEmail: 0 });
  const [usersLoaded, setUsersLoaded] = useState(false); // Indique si les users ont déjà été chargés
  const [loadingUsers, setLoadingUsers] = useState(false); // Chargement des users
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCommercant, setSelectedCommercant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('boutiques'); // 'comptes' ou 'boutiques'
  const [searchTerm, setSearchTerm] = useState(''); // Barre de recherche
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // Debounce 300ms
  const [filter, setFilter] = useState('all'); // Filtre statut pour users
  const [filterFamily, setFilterFamily] = useState('all'); // Filtre famille d'activité
  const [filterVille, setFilterVille] = useState('all'); // Filtre ville pour commerçants
  const [filterStatut, setFilterStatut] = useState('all'); // Filtre statut pour commerçants
  const [filterCompte, setFilterCompte] = useState('all'); // all | linked | missing
  const [sortBy, setSortBy] = useState('nom'); // Colonne de tri
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' ou 'desc'
  const [currentPage, setCurrentPage] = useState(1); // Page actuelle
  const [itemsPerPage, setItemsPerPage] = useState(25); // Items par page (25 par défaut)
  const [showAddModal, setShowAddModal] = useState(false);
  const [isCreatingCommercant, setIsCreatingCommercant] = useState(false);
  const [createFormBanner, setCreateFormBanner] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCommercant, setEditingCommercant] = useState(null);
  const [validationErrors, setValidationErrors] = useState({}); // Erreurs validation commerçant
  const [userValidationErrors, setUserValidationErrors] = useState({}); // Erreurs validation user
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCommercant, setDeletingCommercant] = useState(null);
  /** 'commercant' (onglet Boutiques) ou 'user' (onglet Comptes) — ne pas déduire via categorie */
  const [deleteEntityType, setDeleteEntityType] = useState('commercant');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingCommercant, setDeactivatingCommercant] = useState(null);
  const [permissionsCommercant, setPermissionsCommercant] = useState(null);
  const [commercantPermissions, setCommercantPermissions] = useState([]);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    password: ''
  });
  const [horaires, setHoraires] = useState([]);
  const [openActionsMenuId, setOpenActionsMenuId] = useState(null); // ID du commerçant dont le menu est ouvert

  const [newCommercant, setNewCommercant] = useState(buildEmptyNewCommercantState);
  /** true si l’utilisateur a modifié le CategoryPicker dans la modale d’édition */
  const [editCategoriesDirty, setEditCategoriesDirty] = useState(false);
  const [editCategoriesBaseline, setEditCategoriesBaseline] = useState(null);

  // Charger les commerçants et les utilisateurs au mount initial
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(''); // Réinitialiser l'erreur
        // Charger les commerçants et les utilisateurs en parallèle
        const [commercantsData, summaryData] = await Promise.all([
          apiService.getCommercants({ pageSize: 500, includeInactive: 'true' }),
          apiService.getCommercantsAccountsSummary().catch(err => {
            if (!isProduction) console.error('Accounts summary error:', err);
            return { items: [], stats: { total: 0, linked: 0, missing: 0, noEmail: 0 } };
          })
        ]);
        
        const commercantsArray = normalizeCommercantsResponse(commercantsData);
        setCommercants(commercantsArray);
        setAccountsSummary(summaryData?.items || []);
        setAccountsStats(summaryData?.stats || { total: 0, linked: 0, missing: 0, noEmail: 0 });
        setUsers(
          (summaryData?.items || [])
            .filter((item) => item.user)
            .map((item) => item.user)
        );
        setUsersLoaded(true);
      } catch (err) {
        const errorMessage = err?.message || err?.error?.message || 'Erreur lors du chargement des données';
        setError(errorMessage);
        console.error('❌ [Commercants] Erreur chargement:', err);
        console.error('❌ [Commercants] Erreur détails:', {
          message: err.message,
          status: err.status,
          data: err.data,
          response: err.response,
          stack: err.stack
        });
        toast.error(`Erreur: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const reloadAccountsSummary = async () => {
    try {
      const summaryData = await apiService.getCommercantsAccountsSummary();
      setAccountsSummary(summaryData?.items || []);
      setAccountsStats(summaryData?.stats || { total: 0, linked: 0, missing: 0, noEmail: 0 });
      setUsers(
        (summaryData?.items || [])
          .filter((item) => item.user)
          .map((item) => item.user)
      );
    } catch (err) {
      if (!isProduction) console.error('Erreur rechargement récap comptes:', err);
    }
  };

  const handleViewCommercant = async (id) => {
    try {
      const data = await apiService.getCommercant(id);
      setSelectedCommercant(data);
      setShowModal(true);
    } catch (err) {
      if (!isProduction) console.error('Error fetching commerçant:', err);
    }
  };

  const handleViewUser = async (id) => {
    try {
      const data = await apiService.getUser(id);
      setSelectedCommercant(data);
      setShowModal(true);
    } catch (err) {
      if (!isProduction) console.error('Error fetching user:', err);
    }
  };

  const handleEditUser = async (user) => {
    try {
      setEditingUser(user);
      setEditUserData({
        prenom: user.prenom || '',
        nom: user.nom || '',
        email: user.email || '',
        telephone: user.telephone || '',
        password: '' // Toujours vide pour la sécurité
      });
      setShowEditUserModal(true);
    } catch (err) {
      if (!isProduction) console.error('Error preparing user edit:', err);
    }
  };

  const handleSaveUser = async () => {
    // Validation côté client
    const errors = validateUser(editUserData);
    if (Object.keys(errors).length > 0) {
      setUserValidationErrors(errors);
      toast.warning('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    // Réinitialiser les erreurs de validation
    setUserValidationErrors({});
    
    // Sauvegarder l'état actuel pour rollback si erreur
    const previousUsers = [...users];
    const userId = editingUser.id;
    const previousUser = { ...editingUser };
    
    try {
      const updateData = { ...editUserData };
      
      // Si le mot de passe est vide, ne pas l'envoyer
      if (!updateData.password.trim()) {
        delete updateData.password;
      }
      
      // Optimistic update : mettre à jour immédiatement dans le state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId ? { ...u, ...updateData } : u
        )
      );
      
      // Fermer le modal immédiatement
      setShowEditUserModal(false);
      setEditingUser(null);
      setEditUserData({ prenom: '', nom: '', email: '', telephone: '', password: '' });
      
      // Appel API en arrière-plan
      await apiService.updateUser(userId, updateData);
      await reloadAccountsSummary();
      
      toast.success('Utilisateur mis à jour avec succès !');
    } catch (err) {
      // Rollback en cas d'erreur
      setUsers(previousUsers);
      if (!isProduction) console.error('Error updating user:', err);
      toast.error('Erreur lors de la mise à jour de l\'utilisateur');
      // Rouvrir le modal pour permettre de réessayer
      setEditingUser(previousUser);
      setEditUserData({
        prenom: previousUser.prenom || '',
        nom: previousUser.nom || '',
        email: previousUser.email || '',
        telephone: previousUser.telephone || '',
        password: ''
      });
      setShowEditUserModal(true);
    }
  };

  const handleCancelEditUser = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    setEditUserData({ prenom: '', nom: '', email: '', telephone: '', password: '' });
    setUserValidationErrors({});
  };

  const handleToggleActif = async (commercant) => {
    const previousActifStatus = commercant.actif;
    const newActifStatus = !previousActifStatus;
    
    try {
      // Optimistic update
      setCommercants(prev => 
        prev.map(c => 
          c.id === commercant.id ? { ...c, actif: newActifStatus } : c
        )
      );
      
      // Appel API
      console.log('[Toggle Actif] Avant appel API:', { 
        commercantId: commercant.id, 
        newActifStatus,
        previousActifStatus 
      });
      
      const response = await apiService.updateCommercant(commercant.id, { actif: newActifStatus });
      
      console.log('[Toggle Actif] Réponse API complète:', response);
      
      // Mettre à jour avec la réponse du serveur pour être sûr
      if (response && response.commercant) {
        const updatedActif = response.commercant.actif;
        console.log('[Toggle Actif] Nouveau statut actif depuis API:', updatedActif);
        setCommercants(prev => {
          const updated = prev.map(c => 
            c.id === commercant.id ? { ...c, actif: updatedActif } : c
          );
          console.log('[Toggle Actif] State mis à jour:', updated.find(c => c.id === commercant.id)?.actif);
          return updated;
        });
      } else {
        // Si la réponse ne contient pas commercant, on garde le nouveau statut
        console.warn('[Toggle Actif] Pas de réponse commercant, on garde le statut optimiste:', newActifStatus);
      }
      
      toast.success(
        `Commerçant ${newActifStatus ? 'activé' : 'désactivé'} avec succès`
      );
    } catch (err) {
      // Rollback en cas d'erreur
      setCommercants(prev => 
        prev.map(c => 
          c.id === commercant.id ? { ...c, actif: previousActifStatus } : c
        )
      );
      console.error('Erreur mise à jour statut actif:', err);
      toast.error(err?.message || 'Erreur lors de la mise à jour du statut');
    }
  };

  // Validation côté client pour commerçant
  const validateCommercant = (commercant) => {
    const errors = {};

    if (!commercant.nom || commercant.nom.trim().length < 2) {
      errors.nom = 'Le nom doit contenir au moins 2 caractères';
    }

    const siretDigits = String(commercant.siret || '').replace(/\D/g, '');
    if (siretDigits && siretDigits.length !== 14) {
      errors.siret = 'Le SIRET doit contenir exactement 14 chiffres';
    }

    if (!commercant.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(commercant.email)) {
      errors.email = 'Veuillez saisir une adresse email valide';
    }

    if (commercant.telephone && commercant.telephone.trim().length > 0) {
      // Validation téléphone basique (10 chiffres minimum)
      const phoneDigits = commercant.telephone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.telephone = 'Le numéro de téléphone doit contenir au moins 10 chiffres';
      }
    }

    if (!commercant.ville || commercant.ville.trim().length < 2) {
      errors.ville = 'La ville doit contenir au moins 2 caractères';
    }

    const cpCheck = validateFrenchPostalCode(commercant.codePostal);
    if (!cpCheck.ok) {
      errors.codePostal = cpCheck.error;
    }

    if (commercant.siteWeb && commercant.siteWeb.trim().length > 0) {
      // Validation URL basique
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(commercant.siteWeb)) {
        errors.siteWeb = 'Veuillez saisir une URL valide (ex: https://example.com)';
      }
    }

    if (editCategoriesDirty) {
      const catCheck = validateCategorySelection({
        categorie: commercant.categorie,
        categories: commercant.categories,
      });
      if (!catCheck.ok) {
        errors.categories = catCheck.error;
      }
    }

    return errors;
  };

  // Validation côté client pour utilisateur
  const validateUser = (user) => {
    const errors = {};

    if (!user.prenom || user.prenom.trim().length < 2) {
      errors.prenom = 'Le prénom doit contenir au moins 2 caractères';
    }

    if (!user.nom || user.nom.trim().length < 2) {
      errors.nom = 'Le nom doit contenir au moins 2 caractères';
    }

    if (!user.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
      errors.email = 'Veuillez saisir une adresse email valide';
    }

    if (user.telephone && user.telephone.trim().length > 0) {
      const phoneDigits = user.telephone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.telephone = 'Le numéro de téléphone doit contenir au moins 10 chiffres';
      }
    }

    // Validation mot de passe si fourni
    if (user.password && user.password.trim().length > 0 && user.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    return errors;
  };

  /** Validation formulaire création commerçant + gérant */
  const validateCreateCommercantForm = () => {
    const commercantErrors = {};
    const userErrors = {};

    if (!newCommercant.nomBoutique?.trim() || newCommercant.nomBoutique.trim().length < 2) {
      commercantErrors.nomBoutique = 'Le nom doit contenir au moins 2 caractères';
    }

    const siretDigits = String(newCommercant.siret || '').replace(/\D/g, '');
    if (siretDigits.length !== 14) {
      commercantErrors.siret = 'Le SIRET doit contenir exactement 14 chiffres';
    }

    if (newCommercant.emailBoutique?.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCommercant.emailBoutique.trim())) {
        commercantErrors.emailBoutique = 'Adresse e-mail de la boutique invalide';
      }
    }

    if (newCommercant.telephoneBoutique?.trim()) {
      const phoneDigits = newCommercant.telephoneBoutique.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        commercantErrors.telephoneBoutique = 'Le téléphone de la boutique doit contenir au moins 10 chiffres';
      }
    } else {
      commercantErrors.telephoneBoutique = 'Le téléphone de la boutique est obligatoire';
    }

    if (!newCommercant.rue?.trim()) {
      commercantErrors.rue = 'L\'adresse est obligatoire';
    }

    if (!newCommercant.ville?.trim() || newCommercant.ville.trim().length < 2) {
      commercantErrors.ville = 'La ville est obligatoire';
    }

    const cpCreate = validateFrenchPostalCode(newCommercant.codePostal);
    if (!cpCreate.ok) {
      commercantErrors.codePostal = cpCreate.error;
    }

    if (newCommercant.siteWeb?.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      if (!urlPattern.test(newCommercant.siteWeb.trim())) {
        commercantErrors.siteWeb = 'URL du site web invalide';
      }
    }

    const catCheck = validateCategorySelection({
      categorie: newCommercant.categorie,
      categories: newCommercant.categories,
    });
    if (!catCheck.ok) {
      commercantErrors.categories = catCheck.error;
    }

    const userPayload = {
      prenom: newCommercant.prenomGerant?.trim() || '',
      nom: newCommercant.nomGerant?.trim() || '',
      email: newCommercant.emailGerant?.trim().toLowerCase() || '',
      telephone: newCommercant.telephoneGerant?.trim() || '',
      password: newCommercant.password || '',
    };
    Object.assign(userErrors, validateUser(userPayload));

    if (!newCommercant.password?.trim()) {
      userErrors.password = 'Le mot de passe est obligatoire (6 caractères minimum)';
    } else if (newCommercant.password.length < 6) {
      userErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (newCommercant.telephoneGerant?.includes('@')) {
      userErrors.telephoneGerant = 'Ce champ doit être un numéro de téléphone, pas une adresse e-mail (vérifiez l\'autocomplétion du navigateur)';
    }

    return { commercantErrors, userErrors };
  };

  const handleEditCommercant = (commercantId) => {
    const commercantsArray = Array.isArray(commercants) ? commercants : [];
    const commercant = commercantsArray.find(c => c.id === commercantId);
    const normalized = normalizeCategorySelection({
      categorie: commercant?.categorie,
      categories: commercant?.categories,
    });
    setEditingCommercant({
      ...commercant,
      categorie: normalized.categorie,
      categories: normalized.categories,
      imprimanteActive: Boolean(
        commercant.imprimanteActive ?? commercant.has_printer
      ),
      imprimanteNumeroSerie:
        commercant.imprimanteNumeroSerie || commercant.printer_sn || '',
      imprimanteAppId:
        commercant.imprimanteAppId || commercant.printer_app_id || '',
      // Write-only : ne jamais préremplir la clé depuis l’API
      imprimanteAppKey: '',
      imprimerCommandeValidee:
        commercant.imprimerCommandeValidee ??
        commercant.print_when_validated_order ??
        true,
      imprimerCommandeAcceptee:
        commercant.imprimerCommandeAcceptee ??
        commercant.print_when_accepted_order ??
        false,
    });
    setEditCategoriesBaseline(normalized);
    setEditCategoriesDirty(false);
    setValidationErrors({});
    setShowEditModal(true);
  };

  const handleUpdateCommercant = async (e) => {
    e.preventDefault();
    
    // Validation côté client
    const errors = validateCommercant(editingCommercant);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.warning('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    // Réinitialiser les erreurs de validation
    setValidationErrors({});
    
    // Sauvegarder l'état actuel pour rollback si erreur
    const commercantsArray = Array.isArray(commercants) ? commercants : [];
    const previousCommercants = [...commercantsArray];
    const commercantId = editingCommercant.id;
    const previousCommercant = { ...editingCommercant };
    
    try {
      const cpNormalized = validateFrenchPostalCode(editingCommercant.codePostal);
      const categoryFields = buildEditCategoriesPayload({
        dirty: editCategoriesDirty,
        categorie: editingCommercant.categorie,
        categories: editingCommercant.categories,
      });
      const siretDigits = String(editingCommercant.siret || '').replace(/\D/g, '');
      const updateData = {
        nom: editingCommercant.nom,
        siret: siretDigits || null,
        description: editingCommercant.description,
        ...categoryFields,
        rue: editingCommercant.rue,
        ville: editingCommercant.ville,
        codePostal: cpNormalized.ok ? cpNormalized.value : editingCommercant.codePostal,
        telephone: editingCommercant.telephone,
        email: editingCommercant.email,
        logo: editingCommercant.logo || null,
        siteWeb: editingCommercant.siteWeb,
        tempsLivraisonMin: editingCommercant.tempsLivraisonMin,
        tempsLivraisonMax: editingCommercant.tempsLivraisonMax,
        fraisLivraison: editingCommercant.fraisLivraison,
        livraison: editingCommercant.livraison,
        clickAndCollect: editingCommercant.clickAndCollect,
        paiementEnLigne: editingCommercant.paiementEnLigne,
        paiementSurPlace: editingCommercant.paiementSurPlace,
        imprimanteActive: Boolean(editingCommercant.imprimanteActive),
        imprimanteNumeroSerie: editingCommercant.imprimanteNumeroSerie?.trim() || null,
        imprimanteAppId: editingCommercant.imprimanteAppId?.trim() || null,
        imprimerCommandeValidee: editingCommercant.imprimerCommandeValidee !== false,
        imprimerCommandeAcceptee: Boolean(editingCommercant.imprimerCommandeAcceptee),
      };
      // App Key write-only : n’envoyer que si saisie
      if (editingCommercant.imprimanteAppKey?.trim()) {
        updateData.imprimanteAppKey = editingCommercant.imprimanteAppKey.trim();
      }

      // Optimistic update : mettre à jour immédiatement dans le state
      setCommercants(prevCommercants =>
        prevCommercants.map(c =>
          c.id === commercantId ? { ...c, ...updateData } : c
        )
      );

      // Fermer le modal immédiatement
      setShowEditModal(false);
      setEditingCommercant(null);
      setEditCategoriesDirty(false);
      setEditCategoriesBaseline(null);

      // Appel API en arrière-plan
      await apiService.updateCommercant(commercantId, updateData);
      
      toast.success('Commerçant mis à jour avec succès !');
      
    } catch (error) {
      // Rollback en cas d'erreur
      setCommercants(previousCommercants);
      if (!isProduction) console.error('Erreur mise à jour commerçant:', error);
      toast.error('Erreur lors de la mise à jour du commerçant');
      // Rouvrir le modal pour permettre de réessayer
      setEditingCommercant(previousCommercant);
      setShowEditModal(true);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingCommercant(null);
    setDeleteEntityType('commercant');
  };

  const handleDeleteCommercant = (commercantId) => {
    const commercantsArray = Array.isArray(commercants) ? commercants : [];
    const commercant = commercantsArray.find(c => c.id === commercantId);
    setDeleteEntityType('commercant');
    setDeletingCommercant(commercant);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (password, confirmation) => {
    try {
      if (deleteEntityType === 'commercant') {
        await apiService.deleteCommercant(deletingCommercant.id, password, confirmation);
        const commercantsArray = Array.isArray(commercants) ? commercants : [];
        setCommercants(commercantsArray.filter(c => c.id !== deletingCommercant.id));
        toast.success('Commerçant supprimé avec succès');
      } else {
        await apiService.deleteUser(deletingCommercant.id, password, confirmation);
        setUsers(users.filter(u => u.id !== deletingCommercant.id));
        toast.success('Utilisateur supprimé avec succès');
      }

      closeDeleteModal();
    } catch (err) {
      if (!isProduction) console.error('Erreur suppression:', err);
      const message = err?.message || err?.error || 'Erreur lors de la suppression';
      throw new Error(message);
    }
  };

  const handleManagePermissions = async (commercantId) => {
    try {
      const commercantsArray = Array.isArray(commercants) ? commercants : [];
      const commercant = commercantsArray.find(c => c.id === commercantId);
      if (!commercant) {
        toast.error('Commerçant non trouvé');
        return;
      }
      
      const permissions = await apiService.getCommercantPermissions(commercantId);
      
      setPermissionsCommercant(commercant);
      setCommercantPermissions(permissions.map(p => p.permission));
      setShowPermissionsModal(true);
    } catch (err) {
      if (!isProduction) console.error('Erreur récupération permissions:', err);
      toast.error('Erreur lors de la récupération des permissions');
    }
  };

      const handleSavePermissions = async (permissions) => {
        try {
          const commercantId = permissionsCommercant.id;
          await apiService.updateCommercantPermissions(commercantId, permissions);
          const hasPos = hasAnyPosPermission(permissions);
          toast.success('Permissions mises à jour avec succès !');
          setShowPermissionsModal(false);
          setPermissionsCommercant(null);
          setCommercantPermissions([]);
          // Optimistic immédiat, puis resync liste (évite un badge Caisse fantôme si le cache API était stale).
          setCommercants((prev) =>
            (Array.isArray(prev) ? prev : []).map((c) =>
              c.id === commercantId
                ? { ...c, moduleCaisse: hasPos }
                : c,
            ),
          );
          try {
            const commercantsData = await apiService.getCommercants({
              pageSize: 500,
              includeInactive: 'true',
            });
            setCommercants(normalizeCommercantsResponse(commercantsData));
          } catch (reloadErr) {
            if (!isProduction) console.warn('Resync liste après permissions:', reloadErr);
          }
        } catch (err) {
          if (!isProduction) console.error('Erreur sauvegarde permissions:', err);
          toast.error('Erreur lors de la sauvegarde des permissions');
        }
      };


  const openCommercantAsAdmin = (commercantId, search = '') => {
    // Sortie assistance si active (évite un token orphelin).
    try {
      clearAssistanceSession();
    } catch { /* ignore */ }
    if (setImpersonatedUser) setImpersonatedUser(null);
    navigate(`/commercants/${commercantId}${search}`);
  };

  const handleImpersonate = async (commercantId) => {
    try {
      const { context, commercant } = await apiService.startAssistance(commercantId);
      if (setImpersonatedUser && context) {
        setImpersonatedUser(context);
      }
      toast.success(`Mode assistance : ${commercant?.nom || context.commercantNom}`);
      navigate(`/commercants/${commercantId}?tab=overview`);
    } catch (err) {
      if (!isProduction) console.error('Erreur assistance:', err);
      toast.error(err?.message || 'Erreur lors du démarrage du mode assistance');
    }
  };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    setDeleteEntityType('user');
    setDeletingCommercant(user);
    setShowDeleteModal(true);
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'OUVERT':
      case 'ACTIF':
        return 'bg-green-100 text-green-800';
      case 'FERME':
      case 'INACTIF':
        return 'bg-red-100 text-red-800';
      case 'EN_PAUSE':
      case 'SUSPENDU':
        return 'bg-yellow-100 text-yellow-800';
      case 'MAINTENANCE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (niveau) => {
    switch (niveau) {
      case 'BRONZE':
        return 'bg-orange-100 text-orange-800';
      case 'ARGENT':
        return 'bg-gray-100 text-gray-800';
      case 'OR':
        return 'bg-yellow-100 text-yellow-800';
      case 'PLATINE':
        return 'bg-blue-100 text-blue-800';
      case 'DIAMANT':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction de tri
  const handleSort = (column) => {
    if (sortBy === column) {
      // Si on clique sur la même colonne, on inverse l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne, on trie par ordre croissant
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const filteredCompteItems = accountsSummary.filter((item) => {
    if (filter === 'all') return true;
    if (filter === 'linked') return item.status === 'LINKED';
    if (filter === 'missing') return item.status === 'MISSING';
    if (filter === 'no_email') return item.status === 'NO_EMAIL';
    if (filter === 'active') return item.status === 'LINKED' && item.user?.statut === 'ACTIF';
    if (filter === 'inactive') return item.status === 'LINKED' && item.user?.statut === 'INACTIF';
    if (filter === 'suspended') return item.status === 'LINKED' && item.user?.statut === 'SUSPENDU';
    return true;
  });

  const getCompteStatusBadge = (status) => {
    switch (status) {
      case 'LINKED':
        return { label: 'Compte lié', className: 'bg-green-100 text-green-800' };
      case 'MISSING':
        return { label: 'Compte manquant', className: 'bg-amber-100 text-amber-800' };
      case 'NO_EMAIL':
        return { label: 'Email boutique manquant', className: 'bg-red-100 text-red-800' };
      default:
        return { label: status, className: 'bg-gray-100 text-gray-800' };
    }
  };

  // S'assurer que commercants est toujours un tableau
  const commercantsArray = Array.isArray(commercants) ? commercants : [];
  const accountsByCommercantId = useMemo(
    () => buildAccountsByCommercantId(accountsSummary),
    [accountsSummary]
  );

  // Calculer les valeurs uniques pour les filtres
  const villes = [...new Set(commercantsArray.map(c => c.ville).filter(Boolean))].sort();
  const familyFilterOptions = getFilterOptionsByFamily();

  // Calculer les commerçants filtrés, triés et paginés
  const filteredAndSortedCommercants = commercantsArray
    .filter(commercant => {
      // Filtre recherche (debounce)
      if (debouncedSearchTerm) {
        const search = debouncedSearchTerm.toLowerCase();
        const matchesSearch = (
          commercant.nom?.toLowerCase().includes(search) ||
          commercant.ville?.toLowerCase().includes(search) ||
          commercant.categorie?.toLowerCase().includes(search) ||
          commercant.email?.toLowerCase().includes(search)
        );
        if (!matchesSearch) return false;
      }

      if (!commercantMatchesFamilyFilter(commercant, filterFamily)) {
        return false;
      }

      // Filtre ville
      if (filterVille !== 'all' && commercant.ville !== filterVille) {
        return false;
      }

      // Filtre statut (combine statut ET actif)
      if (filterStatut !== 'all') {
        if (filterStatut === 'actif') {
          // Actif = statut OUVERT ET actif = true
          if (commercant.statut !== 'OUVERT' || commercant.actif !== true) {
            return false;
          }
        }
        if (filterStatut === 'inactif') {
          // Inactif = actif = false (peu importe le statut)
          if (commercant.actif !== false) {
            return false;
          }
        }
        if (filterStatut === 'en_pause' && commercant.statut !== 'EN_PAUSE') {
          return false;
        }
        if (filterStatut === 'maintenance' && commercant.statut !== 'MAINTENANCE') {
          return false;
        }
      }

      const accountSt = getAccountStatusForCommercant(accountsByCommercantId, commercant.id);
      if (filterCompte === 'linked' && accountSt !== 'LINKED') return false;
      if (filterCompte === 'missing' && accountSt !== 'MISSING' && accountSt !== 'NO_EMAIL') {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';
      
      // Conversion en string pour comparaison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      // Tri numérique pour certains champs (note, nombreAvis, etc.)
      if (sortBy === 'note' || sortBy === 'nombreAvis') {
        aValue = parseFloat(a[sortBy] || 0);
        bValue = parseFloat(b[sortBy] || 0);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // Calculer la pagination
  const totalPages = Math.ceil(filteredAndSortedCommercants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCommercants = filteredAndSortedCommercants.slice(startIndex, endIndex);

  // Réinitialiser à la page 1 si recherche ou filtres changent (via useEffect)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filter, filterFamily, filterVille, filterStatut, filterCompte]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commerçants</h1>
            <p className="text-gray-600">Gestion des commerçants et de leurs comptes</p>
          </div>
        </div>
        <div className="card">
          <div className="overflow-x-auto">
            <TableSkeleton rows={5} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commerçants</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commerçants</h1>
          <p className="text-gray-600">Gestion des commerçants et de leurs comptes</p>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => {
              setNewCommercant(buildEmptyNewCommercantState());
              setValidationErrors({});
              setUserValidationErrors({});
              setCreateFormBanner('');
              setHoraires([]);
              setShowAddModal(true);
            }}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un commerçant
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="card">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('boutiques')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'boutiques'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Store className="h-4 w-4 mr-2 inline" />
            Boutiques ({commercantsArray.length})
          </button>
          <button
            onClick={() => setActiveTab('comptes')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'comptes'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="h-4 w-4 mr-2 inline" />
            Comptes ({accountsStats.linked}/{accountsStats.total})
          </button>
        </div>
      </div>

      {/* Filtres pour les comptes */}
      {activeTab === 'comptes' && (
        <div className="card space-y-3">
          {accountsStats.missing > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>{accountsStats.missing}</strong> boutique{accountsStats.missing > 1 ? 's' : ''} sans compte de connexion.
                Utilisez l&apos;onglet Identifiants sur la fiche ou le bouton ci-dessous pour créer le compte.
              </span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === 'all'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Toutes les boutiques ({accountsStats.total})
            </button>
            <button
              onClick={() => setFilter('linked')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === 'linked'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Comptes liés ({accountsStats.linked})
            </button>
            <button
              onClick={() => setFilter('missing')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === 'missing'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Comptes manquants ({accountsStats.missing})
            </button>
            <button
              onClick={() => setFilter('no_email')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === 'no_email'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sans email boutique ({accountsStats.noEmail})
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          {activeTab === 'comptes' ? (
            loading ? (
              // Chargement initial (commerçants + users)
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement des utilisateurs...</p>
                </div>
              </div>
            ) : (
              // Table récapitulatif boutique ↔ compte
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Boutique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email boutique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lien compte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compte (gérant)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut compte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompteItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      Aucune boutique ne correspond à ce filtre.
                    </td>
                  </tr>
                ) : (
                  filteredCompteItems.map(({ commercant, user, status }) => {
                    const statusBadge = getCompteStatusBadge(status);
                    return (
                      <tr
                        key={commercant.id}
                        className={`hover:bg-gray-50 ${status === 'MISSING' ? 'bg-amber-50/40' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CommercantListAvatar commercant={commercant} />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{commercant.nom}</div>
                              <div className="text-sm text-gray-500">{commercant.ville || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {commercant.email ? (
                            <span className="font-mono">{commercant.email}</span>
                          ) : (
                            <span className="text-red-600 italic">Non renseigné</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user ? (
                            <div>
                              <div className="font-medium">{user.prenom} {user.nom}</div>
                              <div className="text-gray-500 font-mono text-xs mt-0.5">{user.email}</div>
                              {user.telephone && (
                                <div className="text-gray-500 text-xs mt-0.5">{user.telephone}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.statut)}`}>
                              {user.statut}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user?.dateCreation
                            ? new Date(user.dateCreation).toLocaleDateString('fr-FR')
                            : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {status === 'MISSING' || status === 'NO_EMAIL' ? (
                              <button
                                onClick={() => openCommercantAsAdmin(commercant.id, '?tab=identifiants')}
                                className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium"
                                title="Créer ou associer un compte"
                              >
                                <Key className="h-4 w-4" />
                                <span className="text-xs">Compte</span>
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleViewUser(user.id)}
                                  className="text-primary-600 hover:text-primary-900"
                                  title="Voir les détails"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Modifier"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => openCommercantAsAdmin(commercant.id, '?tab=identifiants')}
                                  className="text-orange-600 hover:text-orange-900"
                                  title="Identifiants / mot de passe"
                                >
                                  <Key className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            )
          ) : (
            // Table des boutiques
            <>
              {/* Barre de recherche pour les boutiques */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="search"
                    name="boutiques-filter"
                    id="commercants-boutiques-search"
                    placeholder="Rechercher par nom, ville ou activité…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    readOnly={showDeleteModal}
                    onFocus={(e) => {
                      if (showDeleteModal) e.target.blur();
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Filtres combinables */}
              <div className="mb-4 flex flex-wrap gap-3 items-center">
                <label className="text-sm font-medium text-gray-700">Filtres :</label>
                
                <select
                  value={filterFamily}
                  onChange={(e) => setFilterFamily(e.target.value)}
                  className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                  aria-label="Filtrer par type d'activité"
                >
                  <option value="all">Toutes les activités</option>
                  {familyFilterOptions.map((opt) => (
                    <option key={opt.familyId} value={opt.familyId}>{opt.label}</option>
                  ))}
                </select>

                {/* Filtre Ville */}
                <select
                  value={filterVille}
                  onChange={(e) => setFilterVille(e.target.value)}
                  className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="all">Toutes les villes</option>
                  {villes.map(ville => (
                    <option key={ville} value={ville}>{ville}</option>
                  ))}
                </select>

                {/* Filtre Statut */}
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                  <option value="en_pause">En pause</option>
                  <option value="maintenance">Maintenance</option>
                </select>

                <select
                  value={filterCompte}
                  onChange={(e) => setFilterCompte(e.target.value)}
                  className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                  aria-label="Filtrer par compte lié"
                >
                  <option value="all">Tous les comptes</option>
                  <option value="linked">Compte lié</option>
                  <option value="missing">Compte manquant</option>
                </select>

                {/* Badges filtres actifs */}
                {(filterFamily !== 'all' || filterVille !== 'all' || filterStatut !== 'all' || filterCompte !== 'all') && (
                  <div className="flex flex-wrap gap-2 ml-auto">
                    {filterFamily !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {familyFilterOptions.find((o) => o.familyId === filterFamily)?.label || filterFamily}
                        <button
                          onClick={() => setFilterFamily('all')}
                          className="hover:text-primary-900"
                          aria-label="Retirer filtre activité"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterVille !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {filterVille}
                        <button
                          onClick={() => setFilterVille('all')}
                          className="hover:text-primary-900"
                          aria-label={`Retirer filtre ville ${filterVille}`}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterStatut !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {filterStatut === 'actif' ? 'Actif' : filterStatut === 'inactif' ? 'Inactif' : filterStatut === 'en_pause' ? 'En pause' : 'Maintenance'}
                        <button
                          onClick={() => setFilterStatut('all')}
                          className="hover:text-primary-900"
                          aria-label={`Retirer filtre statut ${filterStatut}`}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterCompte !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {filterCompte === 'linked' ? 'Compte lié' : 'Compte manquant'}
                        <button
                          type="button"
                          onClick={() => setFilterCompte('all')}
                          className="hover:text-primary-900"
                          aria-label="Retirer filtre compte"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setFilterFamily('all');
                        setFilterVille('all');
                        setFilterStatut('all');
                        setFilterCompte('all');
                      }}
                      className="text-xs text-gray-600 hover:text-gray-900 underline"
                    >
                      Réinitialiser
                    </button>
                  </div>
                )}
              </div>

              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('nom')}
                  >
                    <div className="flex items-center gap-2">
                      Commerçant
                      {sortBy === 'nom' && (
                        sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('categorie')}
                  >
                    <div className="flex items-center gap-2">
                      Catégorie
                      {sortBy === 'categorie' && (
                        sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('statut')}
                  >
                    <div className="flex items-center gap-2">
                      Statut
                      {sortBy === 'statut' && (
                        sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ouvert
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actif
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('note')}
                  >
                    <div className="flex items-center gap-2">
                      Note
                      {sortBy === 'note' && (
                        sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCommercants.map((commercant) => (
                  <tr
                    key={commercant.id}
                    className="hover:bg-primary-50/40 cursor-pointer transition-colors"
                    onClick={() => openCommercantAsAdmin(commercant.id)}
                    title="Voir la fiche du commerçant"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CommercantListAvatar commercant={commercant} />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {commercant.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {commercant.ville}, {commercant.codePostal}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {getCommercantCategoryIds(commercant).map((catId) => (
                          <CommerceMetierBadge
                            key={catId}
                            code={catId}
                            getMetier={getTaxonomyMetier}
                            primary={catId === commercant.categorie}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(commercant.statut)}`}>
                        {commercant.statut}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          commercant.ouvert
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {commercant.ouvert ? 'Ouvert' : 'Fermé'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1 max-w-[140px]">
                        {serviceBadges(commercant).length > 0 ? (
                          serviceBadges(commercant).map((b) => (
                            <span
                              key={b.key}
                              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${b.className}`}
                            >
                              {b.label}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const st = getAccountStatusForCommercant(accountsByCommercantId, commercant.id);
                        const badge = getCompteStatusBadge(st);
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.className}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          commercant.actif 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {commercant.actif ? 'En ligne' : 'Hors ligne'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Si on désactive (passe de actif à inactif), demander confirmation
                            if (commercant.actif) {
                              setDeactivatingCommercant(commercant);
                              setShowDeactivateModal(true);
                            } else {
                              // Si on active (passe de inactif à actif), pas de confirmation nécessaire
                              handleToggleActif(commercant);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            commercant.actif ? 'bg-green-600' : 'bg-gray-300'
                          }`}
                          title={commercant.actif ? 'Désactiver le commerçant' : 'Activer le commerçant'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              commercant.actif ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <span className="font-medium">{commercant.note ? commercant.note.toFixed(1) : '—'}</span>
                        <span className="text-gray-500 ml-1">({commercant.nombreAvis || 0} avis)</span>
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative flex items-center">
                        {/* Bouton principal - Actions */}
                        <button
                          onClick={() => setOpenActionsMenuId(openActionsMenuId === commercant.id ? null : commercant.id)}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Actions"
                        >
                          <MoreVertical className="h-5 w-5" />
                        </button>
                        
                        {/* Menu dropdown */}
                        {openActionsMenuId === commercant.id && (
                          <>
                            {/* Overlay pour fermer en cliquant ailleurs */}
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenActionsMenuId(null)}
                            />
                            
                            {/* Menu */}
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                              <button
                                onClick={() => {
                                  openCommercantAsAdmin(commercant.id);
                                  setOpenActionsMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                                <span>Voir les détails</span>
                              </button>

                              <button
                                onClick={() => {
                                  setOpenActionsMenuId(null);
                                  void handleImpersonate(commercant.id);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <User className="h-4 w-4 text-amber-700" />
                                <span>Se connecter en tant que</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleEditCommercant(commercant.id);
                                  setOpenActionsMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                                <span>Modifier</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleManagePermissions(commercant.id);
                                  setOpenActionsMenuId(null);
                                }}
                                disabled={!canEditPermissions}
                                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                                  canEditPermissions
                                    ? 'text-gray-700 hover:bg-gray-100'
                                    : 'text-gray-400 cursor-not-allowed'
                                }`}
                              >
                                <Award className="h-4 w-4 text-purple-600" />
                                <span>Gérer les permissions</span>
                              </button>
                              
                              <div className="border-t border-gray-200 my-1" />
                              
                              <button
                                onClick={() => {
                                  openCommercantAsAdmin(commercant.id, '?tab=identifiants');
                                  setOpenActionsMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Key className="h-4 w-4 text-orange-600" />
                                <span>Identifiants / Mot de passe</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  openCommercantAsAdmin(commercant.id, '?tab=advanced');
                                  setOpenActionsMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Settings className="h-4 w-4 text-indigo-600" />
                                <span>Paramètres avancés</span>
                              </button>
                              
                              <div className="border-t border-gray-200 my-1" />
                              
                              <button
                                onClick={() => {
                                  handleDeleteCommercant(commercant.id);
                                  setOpenActionsMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Supprimer</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Message si aucun commerçant */}
            {filteredAndSortedCommercants.length === 0 && !loading && (
              <div className="text-center py-12">
                <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg font-medium mb-2">
                  {commercantsArray.length === 0 
                    ? 'Aucune boutique trouvée' 
                    : 'Aucune boutique ne correspond aux filtres'}
                </p>
                <p className="text-gray-500 text-sm">
                  {commercantsArray.length === 0 
                    ? 'Les boutiques seront affichées ici une fois créées.' 
                    : 'Essayez de modifier vos critères de recherche ou de filtres.'}
                </p>
                {commercantsArray.length === 0 && (
                  <button
                    onClick={() => {
              setNewCommercant(buildEmptyNewCommercantState());
              setValidationErrors({});
              setUserValidationErrors({});
              setCreateFormBanner('');
              setHoraires([]);
              setShowAddModal(true);
            }}
                    className="mt-4 btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer la première boutique
                  </button>
                )}
              </div>
            )}
            
            {/* Pagination */}
            {filteredAndSortedCommercants.length > 0 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage <span className="font-medium">{startIndex + 1}</span> à{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredAndSortedCommercants.length)}</span> sur{' '}
                      <span className="font-medium">{filteredAndSortedCommercants.length}</span> résultats
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">
                      Par page:
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="ml-2 rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                    <nav className="flex gap-1">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ««
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ‹
                      </button>
                      <span className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ›
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        »»
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Modal de détails */}
      {showModal && selectedCommercant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {activeTab === 'boutiques' ? selectedCommercant.nom : `${selectedCommercant.prenom} ${selectedCommercant.nom}`}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fermer</span>
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                {activeTab === 'comptes' ? (
                  // Détails compte utilisateur
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Email</h4>
                        <div className="flex items-center text-gray-600">
                          <Mail className="h-4 w-4 mr-1" />
                          {selectedCommercant.email}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Téléphone</h4>
                        <div className="flex items-center text-gray-600">
                          <Phone className="h-4 w-4 mr-1" />
                          {selectedCommercant.telephone}
                        </div>
                      </div>
                    </div>

                    {selectedCommercant.rue && (
                      <div>
                        <h4 className="font-medium text-gray-900">Adresse</h4>
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-1" />
                          {selectedCommercant.rue}, {selectedCommercant.ville} {selectedCommercant.codePostal}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Rôle</h4>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Administrateur Commerçant
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Permissions</h4>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Accès complet
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Statut</h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCommercant.statut)}`}>
                          {selectedCommercant.statut}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Date d'inscription</h4>
                        <p className="text-gray-600">
                          {new Date(selectedCommercant.dateCreation).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Préférences</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedCommercant.notifications && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Notifications activées
                          </span>
                        )}
                        {selectedCommercant.newsletter && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Newsletter activée
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  // Détails boutique
                  <>
                    <div>
                      <h4 className="font-medium text-gray-900">Description</h4>
                      <p className="text-gray-600">{selectedCommercant.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Catégorie</h4>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getCommercantCategoryIds(selectedCommercant).map((catId) => (
                            <CommerceMetierBadge
                              key={catId}
                              code={catId}
                              getMetier={getTaxonomyMetier}
                              primary={catId === selectedCommercant.categorie}
                            />
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Statut</h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCommercant.statut)}`}>
                          {selectedCommercant.statut}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Note</h4>
                        <p className="text-gray-600">
                          {selectedCommercant.note.toFixed(1)}/5 ({selectedCommercant.nombreAvis} avis)
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Temps de livraison</h4>
                        <p className="text-gray-600">
                          {selectedCommercant.tempsLivraisonMin}-{selectedCommercant.tempsLivraisonMax} min
                        </p>
                      </div>
                    </div>

                    {selectedCommercant.rue && (
                      <div>
                        <h4 className="font-medium text-gray-900">Adresse</h4>
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-1" />
                          {selectedCommercant.rue}, {selectedCommercant.ville} {selectedCommercant.codePostal}
                        </div>
                      </div>
                    )}

                    {selectedCommercant.telephone && (
                      <div>
                        <h4 className="font-medium text-gray-900">Contact</h4>
                        <div className="space-y-1">
                          <div className="flex items-center text-gray-600">
                            <Phone className="h-4 w-4 mr-1" />
                            {selectedCommercant.telephone}
                          </div>
                          {selectedCommercant.email && (
                            <div className="flex items-center text-gray-600">
                              <Mail className="h-4 w-4 mr-1" />
                              {selectedCommercant.email}
                            </div>
                          )}
                          {selectedCommercant.siteWeb && (
                            <div className="flex items-center text-gray-600">
                              <Globe className="h-4 w-4 mr-1" />
                              {selectedCommercant.siteWeb}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

    <div>
                      <h4 className="font-medium text-gray-900">Services</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedCommercant.livraison && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Livraison
                          </span>
                        )}
                        {selectedCommercant.clickAndCollect && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Click & Collect
                          </span>
                        )}
                        {selectedCommercant.paiementEnLigne && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            Paiement en ligne
                          </span>
                        )}
                        {selectedCommercant.paiementSurPlace && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            Paiement sur place
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de commerçant */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Ajouter un nouveau commerçant
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setValidationErrors({});
                    setUserValidationErrors({});
                    setCreateFormBanner('');
                    setIsCreatingCommercant(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fermer</span>
                  ×
                </button>
              </div>
              
              <form 
                className="space-y-6"
                noValidate
                autoComplete="off"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setCreateFormBanner('');

                  const { commercantErrors, userErrors } = validateCreateCommercantForm();
                  if (Object.keys(commercantErrors).length > 0 || Object.keys(userErrors).length > 0) {
                    setValidationErrors(commercantErrors);
                    setUserValidationErrors(userErrors);
                    setCreateFormBanner('Veuillez corriger les champs en rouge ci-dessous avant de créer le compte.');
                    toast.warning('Formulaire incomplet ou invalide');
                    e.currentTarget.closest('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                  }

                  setValidationErrors({});
                  setUserValidationErrors({});
                  setIsCreatingCommercant(true);

                  try {
                    // Créer le compte utilisateur gérant
                    const userData = {
                      prenom: newCommercant.prenomGerant?.trim() || '',
                      nom: newCommercant.nomGerant?.trim() || '',
                      email: newCommercant.emailGerant?.trim().toLowerCase() || '',
                      telephone: newCommercant.telephoneGerant?.trim() || '',
                      password: newCommercant.password || '',
                      rue: newCommercant.rue?.trim() || '',
                      ville: newCommercant.ville?.trim() || '',
                      codePostal: validateFrenchPostalCode(newCommercant.codePostal).value || ''
                    };
                    if (!isProduction) console.log('📋 Données utilisateur:', userData);
                    
                    // Créer la boutique — métiers obligatoires, jamais de fallback RESTAURANT
                    const categoryNorm = normalizeCategorySelection({
                      categorie: newCommercant.categorie,
                      categories: newCommercant.categories,
                    });
                    const categorieKey = categoryNorm.categorie;
                    const categoriesList = categoryNorm.categories;
                    const commercantData = {
                      nom: newCommercant.nomBoutique?.trim() || '',
                      siret: String(newCommercant.siret || '').replace(/\D/g, ''),
                      email: newCommercant.emailBoutique?.trim().toLowerCase() || null,
                      telephone: newCommercant.telephoneBoutique?.trim() || null,
                      description: newCommercant.description?.trim() || '',
                      categorie: categorieKey,
                      categories: categoriesList,
                      moduleProduits: newCommercant.moduleProduits,
                      modulePrestations: newCommercant.modulePrestations,
                      priseRDV: newCommercant.priseRDV,
                      priseCreneaux: newCommercant.priseCreneaux,
                      moduleCreneaux: newCommercant.moduleCreneaux,
                      reservationTable: newCommercant.reservationTable,
                      pasGrilleMinutes: newCommercant.pasGrilleMinutes,
                      dureesAutoriseesMinutes: newCommercant.dureesAutoriseesMinutes,
                      logo: newCommercant.logo || null, // URL Cloudinary (plus de base64)
                      rue: newCommercant.rue?.trim() || null,
                      ville: newCommercant.ville?.trim() || null,
                      codePostal: validateFrenchPostalCode(newCommercant.codePostal).value || null,
                      siteWeb: newCommercant.siteWeb?.trim() || null,
                      emailNotificationCommandes: newCommercant.emailNotificationCommandes?.trim().toLowerCase() || null,
                      tempsPreparation: newCommercant.tempsPreparation || 15,
                      tempsLivraisonMin: newCommercant.tempsLivraisonMin || null,
                      tempsLivraisonMax: newCommercant.tempsLivraisonMax || null,
                      fraisLivraison: newCommercant.fraisLivraison || 0,
                      minimumCommande: newCommercant.minimumCommandeLivraison || null,
                      seuilLivraisonGratuite: newCommercant.seuilLivraisonGratuite || null,
                      dureePlageHoraireMinutes: newCommercant.dureePlageHoraireMinutes || null,
                      maxCommandesParPlage: newCommercant.maxCommandesParPlage || null,
                      livraison: newCommercant.livraison,
                      clickAndCollect: newCommercant.clickAndCollect,
                      paiementEnLigne: newCommercant.paiementEnLigne,
                      paiementSurPlace: newCommercant.paiementSurPlace,
                      magasinItinerant: newCommercant.magasinItinerant || false,
                      lieuPrincipal: newCommercant.lieuPrincipal?.trim() || null,
                      imprimanteActive: Boolean(newCommercant.imprimanteActive),
                      imprimanteNumeroSerie: newCommercant.imprimanteNumeroSerie?.trim() || null,
                      imprimanteAppId: newCommercant.imprimanteAppId?.trim() || null,
                      imprimanteAppKey: newCommercant.imprimanteAppKey?.trim() || null,
                      imprimerCommandeValidee: newCommercant.imprimerCommandeValidee !== false,
                      imprimerCommandeAcceptee: Boolean(newCommercant.imprimerCommandeAcceptee),
                    };
                    if (!isProduction) console.log('🏪 Données commerçant:', commercantData);
                    
                    // Transformer les horaires du format frontend vers le format backend
                    const horairesBackend = frontendToBackend(horaires);
                    if (!isProduction) console.log('📅 Horaires transformés:', horairesBackend);
                    
                    // Appeler l'API pour créer le commerçant et l'utilisateur avec les horaires
                    console.log('📡 Appel API en cours...');
                    console.log('📤 Données envoyées:', { userData, commercantData, horaires: horairesBackend });
                    const response = await apiService.createCommercantWithUser(userData, commercantData, horairesBackend);
                    if (!isProduction) console.log('✅ Commerçant créé avec succès:', response);
                    
                    // Vérifier que la réponse contient bien un commerçant
                    if (!response || !response.commercant) {
                      throw new Error('La réponse du serveur ne contient pas de commerçant créé');
                    }
                    
                    // Fermer le modal et recharger les données
                    setShowAddModal(false);
                    setHoraires([]);
                    setNewCommercant(buildEmptyNewCommercantState());
                    
                    const commercantsData = await apiService.getCommercants();
                    const commercantsArray = Array.isArray(commercantsData) 
                      ? commercantsData 
                      : (commercantsData?.data || commercantsData?.commercants || []);
                    setCommercants(commercantsArray);
                    await reloadAccountsSummary();
                    
                    setCreateFormBanner('');
                    toast.success('✅ Commerçant créé avec succès !');
                  } catch (error) {
                    console.error('❌ Erreur création commerçant:', error);
                    console.error('❌ Détails complets de l\'erreur:', {
                      message: error?.message,
                      response: error?.response?.data,
                      status: error?.response?.status,
                      stack: error?.stack
                    });
                    let errorMessage = 'Erreur inconnue';
                    
                    // Extraire le message d'erreur de manière sécurisée
                    if (error && typeof error === 'object') {
                      if (typeof error.message === 'string') {
                        errorMessage = error.message;
                      } else if (error.response?.data) {
                        // Erreur HTTP avec réponse
                        if (typeof error.response.data.message === 'string') {
                          errorMessage = error.response.data.message;
                        } else if (typeof error.response.data.error === 'string') {
                          errorMessage = error.response.data.error;
                        } else if (error.response.data.error && typeof error.response.data.error.message === 'string') {
                          errorMessage = error.response.data.error.message;
                        }
                      } else if (error.data) {
                        // Format alternatif
                        if (typeof error.data.message === 'string') {
                          errorMessage = error.data.message;
                        } else if (typeof error.data.error === 'string') {
                          errorMessage = error.data.error;
                        } else if (error.data.error && typeof error.data.error.message === 'string') {
                          errorMessage = error.data.error.message;
                        }
                      }
                    } else if (typeof error === 'string') {
                      errorMessage = error;
                    }
                    
                    // Réinitialiser les erreurs de validation
                    const newValidationErrors = {};
                    const newUserValidationErrors = {};
                    
                    // Extraire les détails de l'erreur et mapper vers les champs
                    if (error && error.data) {
                      
                      // Mapper les erreurs de validation vers les champs du formulaire
                      if (error.data.details && Array.isArray(error.data.details)) {
                        error.data.details.forEach(detail => {
                          const fieldPath = detail.field;
                          const message = detail.message;
                          
                          // Mapper les champs userData.* vers userValidationErrors
                          if (fieldPath.startsWith('userData.')) {
                            const fieldName = fieldPath.replace('userData.', '');
                            // Mapper les noms de champs
                            if (fieldName === 'prenom') newUserValidationErrors.prenomGerant = message;
                            else if (fieldName === 'nom') newUserValidationErrors.nomGerant = message;
                            else if (fieldName === 'email') newUserValidationErrors.emailGerant = message;
                            else if (fieldName === 'telephone') newUserValidationErrors.telephoneGerant = message;
                            else if (fieldName === 'password') newUserValidationErrors.password = message;
                            else newUserValidationErrors[fieldName] = message;
                          }
                          // Mapper les champs commercantData.* vers validationErrors
                          else if (fieldPath.startsWith('commercantData.')) {
                            const fieldName = fieldPath.replace('commercantData.', '');
                            // Mapper les noms de champs
                            if (fieldName === 'nom') newValidationErrors.nomBoutique = message;
                            else if (fieldName === 'email' || fieldName === 'emailBoutique') newValidationErrors.emailBoutique = message;
                            else if (fieldName === 'telephone' || fieldName === 'telephoneBoutique') newValidationErrors.telephoneBoutique = message;
                            else newValidationErrors[fieldName] = message;
                          }
                          // Champs directs
                          else {
                            newValidationErrors[fieldPath] = message;
                          }
                        });
                        
                        const detailsText = error.data.details.map(d => `${d.field}: ${d.message}`).join(', ');
                        errorMessage += ` (${detailsText})`;
                      } else if (error.data.details && typeof error.data.details === 'string') {
                        errorMessage += `: ${error.data.details}`;
                      }
                    }
                    
                    setValidationErrors(newValidationErrors);
                    setUserValidationErrors(newUserValidationErrors);
                    const bannerParts = [];
                    if (Object.keys(newValidationErrors).length || Object.keys(newUserValidationErrors).length) {
                      bannerParts.push('Certains champs sont invalides.');
                    }
                    if (typeof errorMessage === 'string' && errorMessage) {
                      bannerParts.push(errorMessage);
                    }
                    setCreateFormBanner(bannerParts.join(' ') || 'La création a échoué. Vérifiez les champs ou réessayez.');
                    e.currentTarget.closest('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' });
                    
                    // Gérer les erreurs de validation des horaires
                    if (error.data && error.data.code === 'VALIDATION_ERROR' && error.data.details) {
                      const horairesErrors = error.data.details.map(err => {
                        const joursMapping = { 0: 'Dimanche', 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi' };
                        const jourLabel = joursMapping[err.jour] || `Jour ${err.jour}`;
                        const errMessage = typeof err.message === 'string' ? err.message : String(err.message || 'Erreur inconnue');
                        return `${jourLabel}: ${errMessage}`;
                      }).join('; ');
                      toast.error(`Erreur de validation des horaires : ${horairesErrors}`);
                    } else {
                      // S'assurer que errorMessage est toujours une string
                      const safeErrorMessage = typeof errorMessage === 'string' ? errorMessage : String(errorMessage || 'Erreur inconnue');
                      toast.error(`Erreur lors de la création : ${safeErrorMessage}`);
                    }
                    console.error('Détails complets de l\'erreur:', {
                      message: error.message,
                      status: error.status,
                      data: error.data,
                      fullError: error,
                      mappedErrors: { newValidationErrors, newUserValidationErrors }
                    });
                    // Afficher les détails de validation dans la console pour debug
                    if (error.data && error.data.details) {
                      console.error('📋 Détails de validation:', error.data.details);
                      error.data.details.forEach((detail, index) => {
                        console.error(`  ${index + 1}. ${detail.field}: ${detail.message}`);
                      });
                    }
                  } finally {
                    setIsCreatingCommercant(false);
                  }
                }}
              >
                {createFormBanner && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {createFormBanner}
                  </div>
                )}

                {/* Section 1: Informations de la boutique */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">🏪 Informations de la boutique</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du commerçant *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.nomBoutique}
                        onChange={(e) => {
                          setNewCommercant({...newCommercant, nomBoutique: e.target.value});
                          if (validationErrors.nomBoutique) {
                            setValidationErrors({...validationErrors, nomBoutique: null});
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.nomBoutique 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Indiquez le nom du commerçant"
                        required
                      />
                      {validationErrors.nomBoutique && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.nomBoutique}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SIRET *
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={newCommercant.siret}
                        onChange={(e) => {
                          setNewCommercant({ ...newCommercant, siret: e.target.value });
                          if (validationErrors.siret) {
                            setValidationErrors({ ...validationErrors, siret: null });
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.siret
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="123 456 789 00012"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Numéro SIRET à 14 chiffres de l’établissement.
                      </p>
                      {validationErrors.siret && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.siret}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <CategoryPicker
                        value={newCommercant.categorie}
                        values={newCommercant.categories}
                        onChange={({ categorie, categories, defaults }) => {
                          setNewCommercant((prev) =>
                            mergeCategoryDefaultsIntoState(prev, { categorie, categories, defaults }),
                          );
                          if (validationErrors.categories) {
                            setValidationErrors((prev) => ({ ...prev, categories: null }));
                          }
                        }}
                      />
                      {validationErrors.categories && (
                        <p className="mt-2 text-sm text-red-600">{validationErrors.categories}</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <CommercantLogoField
                      logoUrl={newCommercant.logoPreview || newCommercant.logo}
                      onLogoChange={(url) =>
                        setNewCommercant({
                          ...newCommercant,
                          logo: url,
                          logoPreview: url,
                        })
                      }
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description de la boutique
                    </label>
                    <textarea
                      value={newCommercant.description}
                      onChange={(e) => setNewCommercant({...newCommercant, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Décrivez l'activité, les spécialités, le public visé…"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rue *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.rue}
                        onChange={(e) => setNewCommercant({...newCommercant, rue: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.rue ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Numéro et nom de la voie"
                      />
                      {validationErrors.rue && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.rue}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.codePostal}
                        onChange={(e) => setNewCommercant({...newCommercant, codePostal: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.codePostal ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Indiquez le code postal"
                      />
                      {validationErrors.codePostal && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.codePostal}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ville *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.ville}
                        onChange={(e) => setNewCommercant({...newCommercant, ville: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.ville ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Indiquez la ville"
                      />
                      {validationErrors.ville && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.ville}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone boutique *
                      </label>
                      <input
                        type="tel"
                        value={newCommercant.telephoneBoutique}
                        onChange={(e) => setNewCommercant({...newCommercant, telephoneBoutique: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.telephoneBoutique ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Numéro de téléphone de la boutique"
                        autoComplete="off"
                      />
                      {validationErrors.telephoneBoutique && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.telephoneBoutique}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email boutique
                      </label>
                      <input
                        type="email"
                        value={newCommercant.emailBoutique}
                        onChange={(e) => setNewCommercant({...newCommercant, emailBoutique: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          validationErrors.emailBoutique ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Adresse e-mail de contact de la boutique"
                        autoComplete="off"
                      />
                      {validationErrors.emailBoutique && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.emailBoutique}</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site web
                    </label>
                    <input
                      type="text"
                      value={newCommercant.siteWeb}
                      onChange={(e) => setNewCommercant({...newCommercant, siteWeb: e.target.value})}
                      onBlur={(e) => {
                        let value = e.target.value.trim();
                        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                          value = 'https://' + value;
                          setNewCommercant({...newCommercant, siteWeb: value});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        validationErrors.siteWeb ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Indiquez l'URL du site ou de la page Clickorico (facultatif)"
                    />
                    {validationErrors.siteWeb && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.siteWeb}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      https:// sera ajouté automatiquement si nécessaire
                    </p>
                  </div>

                  <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50/40 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-blue-900">Mode itinérant</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Activez cette option pour préciser une adresse différente sur chaque créneau (food truck, marché, tournée...).
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newCommercant.magasinItinerant}
                          onChange={(e) => setNewCommercant({
                            ...newCommercant,
                            magasinItinerant: e.target.checked
                          })}
                          className="h-4 w-4 rounded border-blue-400 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-blue-900">
                          Commerce itinérant
                        </span>
                      </label>
                    </div>
                    <p className="mt-3 text-xs text-blue-800">
                      {newCommercant.magasinItinerant
                        ? 'Les lieux et adresses configurés dans les horaires seront utilisés comme points de retrait. L’adresse principale reste uniquement l’adresse du commerce.'
                        : 'L’adresse principale sera utilisée comme point de retrait des commandes.'}
                    </p>
                    {newCommercant.magasinItinerant && newCommercant.clickAndCollect && !hasItinerantRetraitAddress(horaires) && (
                      <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <p>
                          Aucun point de retrait itinérant n’est encore configuré. Ajoutez au moins un lieu et une adresse dans les{' '}
                          <a href="#setup-horaires-create" className="font-semibold underline hover:text-amber-950">
                            horaires
                          </a>{' '}
                          avant d’ouvrir les commandes en Click &amp; Collect.
                        </p>
                      </div>
                    )}
                    {newCommercant.magasinItinerant && (
                      <div className="mt-4 space-y-2">
                        <label className="block text-xs font-medium text-blue-800">
                          Lieu principal (optionnel)
                        </label>
                        <input
                          type="text"
                          value={newCommercant.lieuPrincipal}
                          onChange={(e) => setNewCommercant({
                            ...newCommercant,
                            lieuPrincipal: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="Adresse administrative, dépôt, point de contact..."
                        />
                        <p className="text-xs text-blue-700">
                          L’adresse exacte de chaque service se paramètre ensuite dans l’onglet Horaires (midi / soir).
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email notifications commandes
                    </label>
                    <input
                      type="email"
                      value={newCommercant.emailNotificationCommandes}
                      onChange={(e) => setNewCommercant({...newCommercant, emailNotificationCommandes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="E-mail pour recevoir les notifications de commandes"
                    />
                  </div>

                  {/* Options de service (REMONTÉ) */}
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-800 mb-3">Options de service</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newCommercant.livraison}
                            onChange={(e) => setNewCommercant({...newCommercant, livraison: e.target.checked})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">🚚 Livraison</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newCommercant.clickAndCollect}
                            onChange={(e) => setNewCommercant({...newCommercant, clickAndCollect: e.target.checked})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">📦 Click & Collect</span>
                        </label>
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newCommercant.paiementEnLigne}
                            onChange={(e) => setNewCommercant({...newCommercant, paiementEnLigne: e.target.checked})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">💳 Paiement en ligne</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newCommercant.paiementSurPlace}
                            onChange={(e) => setNewCommercant({...newCommercant, paiementSurPlace: e.target.checked})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">💰 Paiement sur place</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Paramètres communs (toujours visibles si livraison OU click&collect) */}
                  {(newCommercant.livraison || newCommercant.clickAndCollect) && (
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-800 mb-3">⚙️ Paramètres généraux</h5>
                      
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Temps de préparation (min) *
                          </label>
                          <input
                            type="number"
                            value={newCommercant.tempsPreparation || 15}
                            onChange={(e) => setNewCommercant({...newCommercant, tempsPreparation: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="5"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Temps nécessaire pour préparer une commande
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Durée plage (min)
                          </label>
                          <input
                            type="number"
                            value={newCommercant.dureePlageHoraireMinutes}
                            onChange={(e) => setNewCommercant({...newCommercant, dureePlageHoraireMinutes: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="15"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Max commandes/plage
                          </label>
                          <input
                            type="number"
                            value={newCommercant.maxCommandesParPlage}
                            onChange={(e) => setNewCommercant({...newCommercant, maxCommandesParPlage: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="1"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Paramètres spécifiques à la livraison (grisés si livraison non cochée) */}
                  {newCommercant.livraison && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h5 className="text-sm font-semibold text-gray-800 mb-3">🚚 Paramètres de livraison</h5>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Temps de livraison (min)
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={newCommercant.tempsLivraisonMin}
                              onChange={(e) => setNewCommercant({...newCommercant, tempsLivraisonMin: parseInt(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              {[15, 30, 45, 60, 75, 90, 105, 120].map(min => (
                                <option key={min} value={min}>{min} min</option>
                              ))}
                            </select>
                            <select
                              value={newCommercant.tempsLivraisonMax}
                              onChange={(e) => setNewCommercant({...newCommercant, tempsLivraisonMax: parseInt(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              {[15, 30, 45, 60, 75, 90, 105, 120].map(max => (
                                <option key={max} value={max}>{max} min</option>
                              ))}
                            </select>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            De {newCommercant.tempsLivraisonMin} à {newCommercant.tempsLivraisonMax} minutes
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frais de livraison (€)
                          </label>
                          <input
                            type="number"
                            value={newCommercant.fraisLivraison}
                            onChange={(e) => setNewCommercant({...newCommercant, fraisLivraison: parseFloat(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Minimum commande livraison (€)
                          </label>
                          <input
                            type="number"
                            value={newCommercant.minimumCommandeLivraison}
                            onChange={(e) => setNewCommercant({...newCommercant, minimumCommandeLivraison: parseFloat(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Seuil livraison gratuite (€)
                          </label>
                          <input
                            type="number"
                            value={newCommercant.seuilLivraisonGratuite}
                            onChange={(e) => setNewCommercant({...newCommercant, seuilLivraisonGratuite: parseFloat(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            0 = désactivé
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 2: Horaires d'ouverture */}
                <div id="setup-horaires-create" className="border-b border-gray-200 pb-6">
                  <HorairesManager
                    horaires={horaires}
                    onSave={(newHoraires) => {
                      setHoraires(newHoraires);
                      // Auto-save pour le formulaire de création
                    }}
                    loading={false}
                    isItinerant={newCommercant.magasinItinerant || false}
                    defaultEditing={true}
                  />
                </div>

                {/* Section 3: Fermetures exceptionnelles */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">🚫 Fermetures exceptionnelles</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de fermeture
                      </label>
                      <input
                        type="date"
                        value={newCommercant.fermetureTemporaireDebut}
                        onChange={(e) => setNewCommercant({...newCommercant, fermetureTemporaireDebut: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de réouverture
                      </label>
                      <input
                        type="date"
                        value={newCommercant.fermetureTemporaireFin}
                        onChange={(e) => setNewCommercant({...newCommercant, fermetureTemporaireFin: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Raison de la fermeture
                    </label>
                    <input
                      type="text"
                      value={newCommercant.raisonFermeture}
                      onChange={(e) => setNewCommercant({...newCommercant, raisonFermeture: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Indiquez la raison de la fermeture"
                    />
                  </div>
                </div>

                {/* Section 4: Configuration imprimante */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">🖨️ Configuration imprimante ticket</h4>
                  
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newCommercant.imprimanteActive}
                        onChange={(e) => setNewCommercant({...newCommercant, imprimanteActive: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Activer l'imprimante ticket</span>
                    </label>
                  </div>
                  
                  {newCommercant.imprimanteActive && (
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Numéro de série
                          </label>
                          <input
                            type="text"
                            value={newCommercant.imprimanteNumeroSerie}
                            onChange={(e) => setNewCommercant({...newCommercant, imprimanteNumeroSerie: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Numéro de série de l'imprimante"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            APP ID
                          </label>
                          <input
                            type="text"
                            value={newCommercant.imprimanteAppId}
                            onChange={(e) => setNewCommercant({...newCommercant, imprimanteAppId: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Identifiant application imprimante"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            APP KEY
                          </label>
                          <input
                            type="text"
                            value={newCommercant.imprimanteAppKey}
                            onChange={(e) => setNewCommercant({...newCommercant, imprimanteAppKey: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Clé application imprimante"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-start">
                            <input
                              type="checkbox"
                              checked={newCommercant.imprimerCommandeValidee}
                              onChange={(e) => setNewCommercant({...newCommercant, imprimerCommandeValidee: e.target.checked})}
                              className="mt-1 mr-2"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-900">
                                Imprimer dès que le client passe sa commande
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Le ticket est imprimé immédiatement après la réception de la commande.
                              </span>
                            </span>
                          </label>
                        </div>
                        <div>
                          <label className="flex items-start">
                            <input
                              type="checkbox"
                              checked={newCommercant.imprimerCommandeAcceptee}
                              onChange={(e) => setNewCommercant({...newCommercant, imprimerCommandeAcceptee: e.target.checked})}
                              className="mt-1 mr-2"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-900">
                                Imprimer uniquement après avoir accepté la commande
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Le ticket est imprimé lorsque le commerçant clique sur «&nbsp;Accepter&nbsp;».
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Section 5: Informations du gérant */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">👤 Informations du gérant</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom du gérant *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.prenomGerant}
                        onChange={(e) => {
                          setNewCommercant({...newCommercant, prenomGerant: e.target.value});
                          if (userValidationErrors.prenomGerant) {
                            setUserValidationErrors({...userValidationErrors, prenomGerant: null});
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          userValidationErrors.prenomGerant 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Prénom du gérant"
                        required
                      />
                      {userValidationErrors.prenomGerant && (
                        <p className="mt-1 text-sm text-red-600">{userValidationErrors.prenomGerant}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du gérant *
                      </label>
                      <input
                        type="text"
                        value={newCommercant.nomGerant}
                        onChange={(e) => {
                          setNewCommercant({...newCommercant, nomGerant: e.target.value});
                          if (userValidationErrors.nomGerant) {
                            setUserValidationErrors({...userValidationErrors, nomGerant: null});
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          userValidationErrors.nomGerant 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Nom du gérant"
                        required
                      />
                      {userValidationErrors.nomGerant && (
                        <p className="mt-1 text-sm text-red-600">{userValidationErrors.nomGerant}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email du gérant *
                      </label>
                      <input
                        type="email"
                        name="gerant-email"
                        id="gerant-email"
                        value={newCommercant.emailGerant}
                        onChange={(e) => {
                          setNewCommercant({...newCommercant, emailGerant: e.target.value});
                          if (userValidationErrors.emailGerant) {
                            setUserValidationErrors({...userValidationErrors, emailGerant: null});
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          userValidationErrors.emailGerant 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Adresse e-mail de connexion du gérant"
                        autoComplete="email"
                      />
                      {userValidationErrors.emailGerant && (
                        <p className="mt-1 text-sm text-red-600">{userValidationErrors.emailGerant}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone du gérant *
                      </label>
                      <input
                        type="tel"
                        name="gerant-telephone"
                        id="gerant-telephone"
                        inputMode="tel"
                        value={newCommercant.telephoneGerant}
                        onChange={(e) => {
                          setNewCommercant({...newCommercant, telephoneGerant: e.target.value});
                          if (userValidationErrors.telephoneGerant) {
                            setUserValidationErrors({...userValidationErrors, telephoneGerant: null});
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          userValidationErrors.telephoneGerant 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-primary-500'
                        }`}
                        placeholder="Téléphone du gérant"
                        autoComplete="tel"
                      />
                      {userValidationErrors.telephoneGerant && (
                        <p className="mt-1 text-sm text-red-600">{userValidationErrors.telephoneGerant}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe temporaire *
                    </label>
                    <input
                      type="password"
                      name="gerant-password"
                      id="gerant-password"
                      value={newCommercant.password}
                      onChange={(e) => {
                        setNewCommercant({...newCommercant, password: e.target.value});
                        if (userValidationErrors.password) {
                          setUserValidationErrors({...userValidationErrors, password: null});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        userValidationErrors.password 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Choisissez un mot de passe provisoire"
                      autoComplete="new-password"
                    />
                    {userValidationErrors.password && (
                      <p className="mt-1 text-sm text-red-600">{userValidationErrors.password}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Le gérant devra changer ce mot de passe lors de sa première connexion
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setValidationErrors({});
                      setUserValidationErrors({});
                      setCreateFormBanner('');
                      setIsCreatingCommercant(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    disabled={isCreatingCommercant}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingCommercant}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isCreatingCommercant ? 'Création en cours…' : 'Créer le compte'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition de commerçant */}
      {showEditModal && editingCommercant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Modifier {editingCommercant.nom}
                </h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setValidationErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fermer</span>
                  ×
                </button>
              </div>
              
              <form onSubmit={handleUpdateCommercant} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du commerçant *
                  </label>
                  <input
                    type="text"
                    value={editingCommercant.nom}
                    onChange={(e) => {
                      setEditingCommercant({...editingCommercant, nom: e.target.value});
                      // Réinitialiser l'erreur pour ce champ lors de la modification
                      if (validationErrors.nom) {
                        setValidationErrors({...validationErrors, nom: null});
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      validationErrors.nom 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    placeholder="Indiquez le nom du commerçant"
                    required
                  />
                  {validationErrors.nom && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.nom}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIRET
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editingCommercant.siret || ''}
                    onChange={(e) => {
                      setEditingCommercant({ ...editingCommercant, siret: e.target.value });
                      if (validationErrors.siret) {
                        setValidationErrors({ ...validationErrors, siret: null });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      validationErrors.siret
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    placeholder="123 456 789 00012"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Numéro SIRET à 14 chiffres de l’établissement. Laisser vide si inconnu.
                  </p>
                  {validationErrors.siret && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.siret}</p>
                  )}
                </div>

                <CommercantLogoField
                  logoUrl={editingCommercant.logo || null}
                  onLogoChange={(url) =>
                    setEditingCommercant({ ...editingCommercant, logo: url })
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editingCommercant.description || ''}
                    onChange={(e) => setEditingCommercant({...editingCommercant, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Décrivez l'activité, les spécialités, le public visé…"
                  />
                </div>

                <div className="mb-4">
                  <CategoryPicker
                    value={editingCommercant.categorie || ''}
                    values={
                      Array.isArray(editingCommercant.categories)
                        ? editingCommercant.categories
                        : []
                    }
                    onChange={({ categorie, categories }) => {
                      const next = normalizeCategorySelection({ categorie, categories });
                      setEditingCommercant({
                        ...editingCommercant,
                        categorie: next.categorie,
                        categories: next.categories,
                      });
                      setEditCategoriesDirty(
                        !editCategoriesBaseline ||
                          !categorySelectionsEqual(editCategoriesBaseline, next),
                      );
                      if (validationErrors.categories) {
                        setValidationErrors({ ...validationErrors, categories: null });
                      }
                    }}
                    showRecap={false}
                  />
                  {validationErrors.categories && (
                    <p className="mt-2 text-sm text-red-600">{validationErrors.categories}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Statut
                    </label>
                    <select
                      value={editingCommercant.statut || 'OUVERT'}
                      onChange={(e) => setEditingCommercant({...editingCommercant, statut: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="OUVERT">Ouvert</option>
                      <option value="FERME">Fermé</option>
                      <option value="MAINTENANCE">Maintenance</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rue
                    </label>
                    <input
                      type="text"
                      value={editingCommercant.rue || ''}
                      onChange={(e) => setEditingCommercant({...editingCommercant, rue: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Numéro et nom de la voie"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville *
                    </label>
                    <input
                      type="text"
                      value={editingCommercant.ville || ''}
                      onChange={(e) => {
                        setEditingCommercant({...editingCommercant, ville: e.target.value});
                        if (validationErrors.ville) {
                          setValidationErrors({...validationErrors, ville: null});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        validationErrors.ville 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Indiquez la ville"
                    />
                    {validationErrors.ville && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.ville}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      value={editingCommercant.codePostal || ''}
                      onChange={(e) => {
                        setEditingCommercant({...editingCommercant, codePostal: e.target.value});
                        if (validationErrors.codePostal) {
                          setValidationErrors({...validationErrors, codePostal: null});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        validationErrors.codePostal 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Indiquez le code postal"
                    />
                    {validationErrors.codePostal && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.codePostal}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={editingCommercant.telephone || ''}
                      onChange={(e) => {
                        setEditingCommercant({...editingCommercant, telephone: e.target.value});
                        if (validationErrors.telephone) {
                          setValidationErrors({...validationErrors, telephone: null});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        validationErrors.telephone 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Numéro de téléphone de la boutique"
                    />
                    {validationErrors.telephone && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.telephone}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={editingCommercant.email || ''}
                      onChange={(e) => {
                        setEditingCommercant({...editingCommercant, email: e.target.value});
                        if (validationErrors.email) {
                          setValidationErrors({...validationErrors, email: null});
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        validationErrors.email 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-primary-500'
                      }`}
                      placeholder="Adresse e-mail de contact de la boutique"
                    />
                    {validationErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site web
                  </label>
                  <input
                    type="url"
                    value={editingCommercant.siteWeb || ''}
                    onChange={(e) => {
                      setEditingCommercant({...editingCommercant, siteWeb: e.target.value});
                      if (validationErrors.siteWeb) {
                        setValidationErrors({...validationErrors, siteWeb: null});
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      validationErrors.siteWeb 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                  {validationErrors.siteWeb && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.siteWeb}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temps de livraison min (min)
                    </label>
                    <input
                      type="number"
                      value={editingCommercant.tempsLivraisonMin || 30}
                      onChange={(e) => setEditingCommercant({...editingCommercant, tempsLivraisonMin: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temps de livraison max (min)
                    </label>
                    <input
                      type="number"
                      value={editingCommercant.tempsLivraisonMax || 45}
                      onChange={(e) => setEditingCommercant({...editingCommercant, tempsLivraisonMax: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frais de livraison (€)
                  </label>
                  <input
                    type="number"
                    value={editingCommercant.fraisLivraison || 0}
                    onChange={(e) => setEditingCommercant({...editingCommercant, fraisLivraison: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingCommercant.livraison || false}
                        onChange={(e) => setEditingCommercant({...editingCommercant, livraison: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">🚚 Livraison</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingCommercant.clickAndCollect || false}
                        onChange={(e) => setEditingCommercant({...editingCommercant, clickAndCollect: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">📦 Click & Collect</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingCommercant.paiementEnLigne || false}
                        onChange={(e) => setEditingCommercant({...editingCommercant, paiementEnLigne: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">💳 Paiement en ligne</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingCommercant.paiementSurPlace || false}
                        onChange={(e) => setEditingCommercant({...editingCommercant, paiementSurPlace: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">💰 Paiement sur place</span>
                    </label>
                  </div>
                </div>

                <CommercantPrinterCockpit
                  commercantId={editingCommercant.id}
                  value={{
                    imprimanteActive: editingCommercant.imprimanteActive,
                    imprimanteNumeroSerie: editingCommercant.imprimanteNumeroSerie,
                    imprimanteAppId: editingCommercant.imprimanteAppId,
                    imprimanteAppKey: editingCommercant.imprimanteAppKey,
                    imprimerCommandeValidee: editingCommercant.imprimerCommandeValidee,
                    imprimerCommandeAcceptee: editingCommercant.imprimerCommandeAcceptee,
                  }}
                  onChange={(partial) =>
                    setEditingCommercant((prev) => ({ ...prev, ...partial }))
                  }
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Mettre à jour
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression sécurisée */}
      {showDeleteModal && deletingCommercant && (
        <DeleteModal
          entity={deletingCommercant}
          entityType={deleteEntityType}
          onClose={closeDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      )}

      {/* Modal de confirmation pour désactivation */}
      {showDeactivateModal && deactivatingCommercant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-6 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Mettre hors ligne
            </h3>
            
            <p className="text-sm text-gray-600 mb-6">
              Êtes-vous sûr de vouloir mettre <strong>{deactivatingCommercant.nom}</strong> hors ligne ?
              <br />
              <br />
              Le commerçant ne sera plus visible sur la plateforme, mais ses données seront conservées.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivatingCommercant(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Annuler
              </button>
              <button 
                onClick={() => {
                  handleToggleActif(deactivatingCommercant);
                  setShowDeactivateModal(false);
                  setDeactivatingCommercant(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition des utilisateurs */}
      {showEditUserModal && editingUser && (
        <EditUserModal
          user={editingUser}
          editData={editUserData}
          canEdit={currentUserRole === 'SUPER_ADMIN' || currentUserRole === 'ADMIN'}
          onSave={handleSaveUser}
          onCancel={handleCancelEditUser}
          onChange={setEditUserData}
        />
      )}

          {/* Modal de gestion des permissions */}
          {showPermissionsModal && permissionsCommercant && (
            <PermissionModal
              commercant={permissionsCommercant}
              permissions={commercantPermissions}
              onClose={() => setShowPermissionsModal(false)}
              onSave={handleSavePermissions}
              readOnly={!canEditPermissions}
            />
          )}

      {/* Toasts (page entière) */}
      <div className="fixed top-4 right-4 z-[60] space-y-2">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>

    </div>
  );
};

// Modal de suppression sécurisée
const DeleteModal = ({ entity, entityType, onClose, onConfirm }) => {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);

  const isCommercant = entityType === 'commercant';
  const displayName = isCommercant
    ? entity.nom
    : [entity.prenom, entity.nom].filter(Boolean).join(' ') || entity.email;

  const handleDelete = async (e) => {
    e?.preventDefault();
    if (confirmation !== 'SUPPRIMER') {
      toast.warning('Veuillez taper "SUPPRIMER" pour confirmer');
      return;
    }
    if (!password.trim()) {
      toast.warning('Mot de passe administrateur requis');
      return;
    }

    setLoading(true);
    try {
      await onConfirm(password, confirmation);
    } catch (error) {
      toast.error(error?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
    >
      <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <h3 id="delete-modal-title" className="text-lg font-medium text-red-600 mb-4">
          ⚠️ Suppression de {displayName}
        </h3>

        <form
          autoComplete="off"
          onSubmit={handleDelete}
          className="space-y-4"
        >
          {/* Leurres anti-autofill navigateur (évite de polluer la barre de recherche) */}
          <input
            type="text"
            name="fake-username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            className="absolute opacity-0 h-0 w-0 pointer-events-none"
            readOnly
          />

          <div>
            <label htmlFor="delete-admin-password" className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe administrateur *
            </label>
            <input
              id="delete-admin-password"
              type="password"
              name="delete-admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Votre mot de passe admin"
            />
          </div>

          <div>
            <label htmlFor="delete-confirmation" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmation *
            </label>
            <input
              id="delete-confirmation"
              type="text"
              name="delete-confirmation"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-lpignore="true"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Tapez 'SUPPRIMER' pour confirmer"
            />
          </div>

          <div className="bg-red-50 p-3 rounded-md">
            <p className="text-sm text-red-700">
              <strong>Attention :</strong> Cette action supprimera définitivement :
              {isCommercant ? (
                <>
                  <br />• Le commerçant et ses données
                  <br />• Tous les produits associés
                  <br />• L&apos;historique des commandes
                  <br />• Les permissions et accès
                </>
              ) : (
                <>
                  <br />• L&apos;utilisateur et ses données
                  <br />• L&apos;historique des commandes
                  <br />• Les points de fidélité
                  <br />• Les préférences utilisateur
                </>
              )}
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn-danger"
              disabled={loading || !password.trim() || confirmation !== 'SUPPRIMER'}
            >
              {loading ? 'Suppression...' : 'Supprimer définitivement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal d'édition des utilisateurs (canEdit = Super Admin / Admin, fourni par le parent)
const EditUserModal = ({ editData, onSave, onCancel, onChange, canEdit = false }) => {
  const isSuperAdmin = canEdit;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Modifier le gérant
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Fermer</span>
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Informations personnelles */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom *
                </label>
                <input
                  type="text"
                  value={editData.prenom}
                  onChange={(e) => onChange({...editData, prenom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  value={editData.nom}
                  onChange={(e) => onChange({...editData, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email de connexion *
              </label>
              <input
                type="email"
                value={editData.email}
                onChange={(e) => onChange({...editData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={!isSuperAdmin}
              />
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                value={editData.telephone}
                onChange={(e) => onChange({...editData, telephone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={!isSuperAdmin}
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={editData.password}
                onChange={(e) => onChange({...editData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Laisser vide pour ne pas changer"
                disabled={!isSuperAdmin}
              />
              <p className="text-xs text-gray-500 mt-1">
                {isSuperAdmin ? 'Laissez vide pour conserver le mot de passe actuel' : 'Seul le Super Admin peut modifier le mot de passe'}
              </p>
            </div>

            {/* Message de restriction */}
            {!isSuperAdmin && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Accès restreint
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Seul le Super Admin peut modifier les informations personnelles et le mot de passe des gérants.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              onClick={onSave}
              disabled={!isSuperAdmin}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                isSuperAdmin 
                  ? 'bg-primary-600 hover:bg-primary-700' 
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isSuperAdmin ? 'Sauvegarder' : 'Accès restreint'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeUsers = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

export default Commercants;